/**
 * Phase 4 — inspections canonical constants.
 *
 * These literal lists power both Postgres enums and Zod parsers across
 * the API, worker, and web app. Adding or removing a value requires a
 * coordinated migration; the lists are the single source of truth for
 * the cross-process contract.
 */

import type { WorkOrderCategory, WorkOrderSeverity } from './work-order-constants.js';

// ---------- enums ----------

export const INSPECTION_APPLIES_TO = ['building', 'unit', 'common_area'] as const;
export type InspectionAppliesTo = (typeof INSPECTION_APPLIES_TO)[number];

export const INSPECTION_APPLIES_TO_LABELS: Record<InspectionAppliesTo, string> = {
  building: 'Whole building',
  unit: 'Per unit',
  common_area: 'Common area',
};

export const INSPECTION_TEMPLATE_STATUSES = ['draft', 'published'] as const;
export type InspectionTemplateStatus = (typeof INSPECTION_TEMPLATE_STATUSES)[number];

export const INSPECTION_STATUSES = [
  'scheduled',
  'in_progress',
  'submitted',
  'cancelled',
] as const;
export type InspectionStatus = (typeof INSPECTION_STATUSES)[number];

export const INSPECTION_STATUS_LABELS: Record<InspectionStatus, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  submitted: 'Submitted',
  cancelled: 'Cancelled',
};

export const INSPECTION_QUESTION_KINDS = [
  'yes_no',
  'pass_fail',
  'rating',
  'short_text',
  'long_text',
  'number',
  'single_select',
  'multi_select',
  'photo',
  'signature',
  'section_header',
] as const;
export type InspectionQuestionKind = (typeof INSPECTION_QUESTION_KINDS)[number];

/** In-app runner layout for checklist-style forms (e.g. DHS unit inspection). */
export const INSPECTION_FORM_LAYOUTS = ['default', 'checklist_table'] as const;
export type InspectionFormLayout = (typeof INSPECTION_FORM_LAYOUTS)[number];

/** PDF export layout variants — templates may offer more than one. */
export const INSPECTION_PDF_LAYOUTS = ['default', 'dhs_official', 'dhs_clean'] as const;
export type InspectionPdfLayout = (typeof INSPECTION_PDF_LAYOUTS)[number];

export const INSPECTION_PDF_LAYOUT_LABELS: Record<InspectionPdfLayout, string> = {
  default: 'Standard summary',
  dhs_official: 'Official form layout',
  dhs_clean: 'Clean summary layout',
};

/** Auto-fill header fields from inspection context when the answer is empty. */
export const INSPECTION_PREFILL_SOURCES = [
  'building.name',
  'building.address',
  'unit.number',
  'inspection.date',
  'inspector.name',
] as const;
export type InspectionPrefillSource = (typeof INSPECTION_PREFILL_SOURCES)[number];

/** Stable keys for programmatic footer/header wiring (defect → WO sync). */
export type InspectionQuestionKey =
  | 'header.client_name'
  | 'header.unit_number'
  | 'header.inspection_date'
  | 'header.case_number'
  | 'header.shelter_name'
  | 'header.address'
  | 'header.adults'
  | 'header.children'
  | 'footer.housekeeping_rating'
  | 'footer.housekeeping_issues'
  | 'footer.unit_repairs_needed'
  | 'footer.work_orders_submitted'
  | 'footer.wo_date_submitted'
  | 'footer.overall_condition'
  | 'footer.client_signature'
  | 'footer.case_manager_signature'
  | 'footer.comments';

export const INSPECTION_QUESTION_KIND_LABELS: Record<InspectionQuestionKind, string> = {
  yes_no: 'Yes / No / N/A',
  pass_fail: 'Pass / Fail',
  rating: 'Rating',
  short_text: 'Short text',
  long_text: 'Long text',
  number: 'Number',
  single_select: 'Single select',
  multi_select: 'Multi select',
  photo: 'Photo',
  signature: 'Signature',
  section_header: 'Section header',
};

export const INSPECTION_SCHEDULE_CADENCES = [
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
  'semi_annually',
  'annually',
  'custom',
] as const;
export type InspectionScheduleCadence = (typeof INSPECTION_SCHEDULE_CADENCES)[number];

export const INSPECTION_SCHEDULE_CADENCE_LABELS: Record<InspectionScheduleCadence, string> = {
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annually: 'Semi-annually',
  annually: 'Annually',
  custom: 'Custom',
};

// ---------- form schema (the JSONB content) ----------

export interface InspectionSelectOption {
  value: string;
  label: string;
  isDefect?: boolean;
}

export type InspectionQuestionBase = {
  /** Optional stable key for auto-fill / footer sync (presets use these). */
  questionKey?: InspectionQuestionKey;
};

export type InspectionQuestion =
  | (InspectionQuestionBase & {
      id: string;
      kind: 'yes_no';
      label: string;
      required?: boolean;
      defectIf?: 'yes' | 'no';
      /** When true, inspectors may answer N/A (never flagged as a defect). */
      allowNa?: boolean;
      helpText?: string;
    })
  | (InspectionQuestionBase & {
      id: string;
      kind: 'pass_fail';
      label: string;
      required?: boolean;
      helpText?: string;
    })
  | (InspectionQuestionBase & {
      id: string;
      kind: 'rating';
      label: string;
      scale: 3 | 5;
      required?: boolean;
      defectAtOrBelow?: number;
      helpText?: string;
    })
  | (InspectionQuestionBase & {
      id: string;
      kind: 'short_text';
      label: string;
      required?: boolean;
      prefillSource?: InspectionPrefillSource;
      helpText?: string;
    })
  | (InspectionQuestionBase & {
      id: string;
      kind: 'long_text';
      label: string;
      required?: boolean;
      helpText?: string;
    })
  | (InspectionQuestionBase & {
      id: string;
      kind: 'number';
      label: string;
      unit?: string;
      min?: number;
      max?: number;
      required?: boolean;
      prefillSource?: InspectionPrefillSource;
      helpText?: string;
    })
  | (InspectionQuestionBase & {
      id: string;
      kind: 'single_select';
      label: string;
      options: InspectionSelectOption[];
      required?: boolean;
      /** Merge org users into the option list at runtime. */
      optionSource?: 'org_users';
      helpText?: string;
    })
  | (InspectionQuestionBase & {
      id: string;
      kind: 'multi_select';
      label: string;
      options: InspectionSelectOption[];
      required?: boolean;
      helpText?: string;
    })
  | (InspectionQuestionBase & {
      id: string;
      kind: 'photo';
      label: string;
      minPhotos?: number;
      maxPhotos?: number;
      required?: boolean;
      helpText?: string;
    })
  | (InspectionQuestionBase & {
      id: string;
      kind: 'signature';
      label: string;
      required?: boolean;
      collectPrintedName?: boolean;
      helpText?: string;
    })
  | { id: string; kind: 'section_header'; label: string };

export interface InspectionSection {
  id: string;
  title: string;
  description?: string;
  questions: InspectionQuestion[];
}

export interface InspectionSchema {
  sections: InspectionSection[];
  /** Runner UI layout. Defaults to `default` (card per question). */
  layout?: InspectionFormLayout;
  /** Primary PDF layout on submit. Defaults to `default`. */
  pdfLayout?: InspectionPdfLayout;
  /** When set, the runner/PDF buttons offer multiple export layouts. */
  pdfLayouts?: InspectionPdfLayout[];
}

// ---------- answer values (per question kind) ----------

export type InspectionAnswerValue =
  | { kind: 'yes_no'; value: 'yes' | 'no' | 'na' | null }
  | { kind: 'pass_fail'; value: 'pass' | 'fail' | null }
  | { kind: 'rating'; value: number | null }
  | { kind: 'short_text'; value: string | null }
  | { kind: 'long_text'; value: string | null }
  | { kind: 'number'; value: number | null }
  | { kind: 'single_select'; value: string | null }
  | { kind: 'multi_select'; value: string[] }
  | { kind: 'photo'; value: null }
  | {
      kind: 'signature';
      value: { attachmentId: string; printedName?: string | null } | null;
    };

// ---------- defect detection ----------

/**
 * Returns true when the answer should be flagged as a defect, based on
 * the rules encoded in the question schema. Centralized here so the
 * runner, the API, and the PDF agree on what counts as a defect.
 *
 * Photo / signature / section_header questions never auto-flag.
 * Multi_select flags when **any** selected option carries `isDefect: true`.
 */
export function isAnswerDefect(
  question: InspectionQuestion,
  value: InspectionAnswerValue,
): boolean {
  if (question.kind !== value.kind) return false;
  switch (question.kind) {
    case 'yes_no': {
      const v = (value as Extract<InspectionAnswerValue, { kind: 'yes_no' }>).value;
      if (!v || v === 'na') return false;
      const trigger = question.defectIf ?? 'no';
      return v === trigger;
    }
    case 'pass_fail': {
      const v = (value as Extract<InspectionAnswerValue, { kind: 'pass_fail' }>).value;
      return v === 'fail';
    }
    case 'rating': {
      const v = (value as Extract<InspectionAnswerValue, { kind: 'rating' }>).value;
      if (v === null || v === undefined) return false;
      if (question.defectAtOrBelow === undefined) return false;
      return v <= question.defectAtOrBelow;
    }
    case 'single_select': {
      const v = (value as Extract<InspectionAnswerValue, { kind: 'single_select' }>).value;
      if (v === null) return false;
      const opt = question.options.find((o) => o.value === v);
      return opt?.isDefect === true;
    }
    case 'multi_select': {
      const v = (value as Extract<InspectionAnswerValue, { kind: 'multi_select' }>).value;
      if (!v.length) return false;
      return question.options.some((o) => o.isDefect === true && v.includes(o.value));
    }
    default:
      return false;
  }
}

// ---------- defect → WO category mapping ----------

/**
 * Section title keyword → work-order category fallback. The defect-review
 * step uses this to pre-fill `category` on the auto-created WO. Order
 * matters: first matching keyword wins. Unmatched sections fall back to
 * `inspection_followup` (the canonical "this came from an inspection"
 * bucket already declared in Phase 2's category enum).
 */
export const INSPECTION_DEFECT_CATEGORY_MAP: ReadonlyArray<{
  keywords: readonly string[];
  category: WorkOrderCategory;
}> = [
  { keywords: ['plumb', 'leak', 'pipe', 'drain', 'water', 'sink', 'toilet'], category: 'plumbing' },
  {
    keywords: ['electric', 'outlet', 'wiring', 'breaker', 'lighting', 'lamp'],
    category: 'electrical',
  },
  { keywords: ['hvac', 'heat', 'cool', 'ac', 'boiler', 'thermostat'], category: 'hvac' },
  {
    keywords: ['appliance', 'fridge', 'refriger', 'stove', 'oven', 'washer', 'dryer'],
    category: 'appliance',
  },
  {
    keywords: ['structur', 'wall', 'ceiling', 'floor', 'door', 'window'],
    category: 'structural',
  },
  { keywords: ['pest', 'roach', 'rat', 'mouse', 'mice', 'rodent', 'bug'], category: 'pest' },
  { keywords: ['clean', 'trash', 'garbage', 'lobby', 'hallway', 'stair'], category: 'cleaning' },
  {
    keywords: [
      'safety',
      'fire',
      'extinguisher',
      'sprinkler',
      'smoke',
      'co ',
      'carbon',
      'emergency',
      'exit',
    ],
    category: 'safety',
  },
];

export function inferDefectCategory(sectionTitle: string): WorkOrderCategory {
  const haystack = sectionTitle.toLowerCase();
  for (const entry of INSPECTION_DEFECT_CATEGORY_MAP) {
    if (entry.keywords.some((kw) => haystack.includes(kw))) return entry.category;
  }
  return 'inspection_followup';
}

// ---------- severity for auto-WOs ----------

/** Default severity for inspection-defect-derived WOs. Caller may override. */
export const INSPECTION_DEFECT_DEFAULT_SEVERITY: WorkOrderSeverity = 'medium';

// ---------- schedule cadence math ----------

export interface InspectionCadenceSpec {
  cadence: InspectionScheduleCadence;
  customIntervalDays: number | null;
}

/**
 * Advance an anchor timestamp by one cadence period. Calendar-aware for
 * monthly+ cadences (so leap years and month-end don't drift); day-based
 * for weekly/biweekly/custom. Returns a new `Date`.
 */
export function advanceByInspectionCadence(anchor: Date, spec: InspectionCadenceSpec): Date {
  const next = new Date(anchor.getTime());
  switch (spec.cadence) {
    case 'weekly':
      next.setUTCDate(next.getUTCDate() + 7);
      return next;
    case 'biweekly':
      next.setUTCDate(next.getUTCDate() + 14);
      return next;
    case 'monthly':
      next.setUTCMonth(next.getUTCMonth() + 1);
      return next;
    case 'quarterly':
      next.setUTCMonth(next.getUTCMonth() + 3);
      return next;
    case 'semi_annually':
      next.setUTCMonth(next.getUTCMonth() + 6);
      return next;
    case 'annually':
      next.setUTCFullYear(next.getUTCFullYear() + 1);
      return next;
    case 'custom': {
      const days = spec.customIntervalDays;
      if (!days || days <= 0) {
        throw new Error('custom cadence requires a positive customIntervalDays');
      }
      next.setUTCDate(next.getUTCDate() + days);
      return next;
    }
  }
}

// ---------- schedule scope (jsonb) ----------

export type InspectionScheduleScope =
  | { kind: 'whole_building'; buildingId: string }
  | { kind: 'all_units_in_building'; buildingId: string }
  | { kind: 'specific_units'; buildingId: string; unitIds: string[] }
  | { kind: 'common_area'; buildingId: string };

// ---------- preset slugs ----------

/** Stable identifiers used by the seed-presets endpoint and tests. */
export const INSPECTION_PRESET_SLUGS = [
  'dhs-unit-inspection',
  'dhs-unit-inspection-official',
  'fire-safety-walk',
  'common-area-cleanliness',
  'move-out-inspection',
] as const;

/** Find a question by its stable `questionKey` within a schema. */
export function findQuestionByKey(
  schema: InspectionSchema,
  key: InspectionQuestionKey,
): InspectionQuestion | undefined {
  for (const section of schema.sections) {
    for (const q of section.questions) {
      if (q.kind !== 'section_header' && q.questionKey === key) return q;
    }
  }
  return undefined;
}
export type InspectionPresetSlug = (typeof INSPECTION_PRESET_SLUGS)[number];

export interface InspectionPreset {
  slug: InspectionPresetSlug;
  name: string;
  description: string;
  appliesTo: InspectionAppliesTo;
  schema: InspectionSchema;
}
